/**
 * LES PAGES PUBLIQUES, EN CHUNKS SÉPARÉS, HYDRATABLES.
 *
 * Deux contraintes qui se contredisent en apparence :
 *   · chaque page publique doit être son propre chunk (un visiteur venu
 *     lire /telecom n'a pas à télécharger la page Tarifs et ses modales) ;
 *   · la page courante est PRÉ-RENDUE et HYDRATÉE (src/index.js), et une
 *     frontière <Suspense> dans l'arbre hydraté, sans marqueur serveur
 *     dans le DOM, fait jeter tout le DOM pré-rendu à React 19 (mesuré le
 *     03/09/2026 : erreur récupérable + nœuds remplacés). Un `lazy` déjà
 *     résolu, SANS frontière, hydrate proprement.
 *
 * D'où `lazyPublic` : le chunk de la route courante est préchargé AVANT
 * l'hydratation (`preloadPublicPage`), et la page se rend alors sans
 * frontière. Les autres pages publiques, atteintes en navigation SPA,
 * montent DANS une frontière : elles ne sont pas hydratées, elles sont
 * rendues à neuf, et la frontière ne gêne rien.
 */
import React, { Suspense, lazy } from 'react';
import { LANDING_SLUGS } from './content/landings';
import { LEGAL_SLUGS } from './content/legal';
import { BE_PILLAR_SLUGS } from './content/bePillarPages';
import { PAGES_DETRESSE_SLUGS } from './content/pagesDetresse';
import { REDIRECTS } from './lib/seo/metadata';

function lazyPublic(loader) {
  const Inner = lazy(loader);
  let ready = false;
  const Page = (props) => (ready
    ? <Inner {...props} />
    : (
      <Suspense fallback={<div data-testid="page-loading" style={{ minHeight: '60vh' }} />}>
        <Inner {...props} />
      </Suspense>
    ));
  Page.preload = () => loader().then((m) => { ready = true; return m; });
  return Page;
}

export const PUBLIC_PAGES = {
  // « / » : la home v3 (design-refs/home-v3). HomeV2 reste dans le dépôt,
  // non routée : revenir en arrière = remettre son import ici et dans App.js.
  HomeV3:           lazyPublic(() => import('./pages/HomeV3')),
  // /attorneys : « Qui se bat pour toi » v3 (design-refs/team-v3-ref).
  // TeamPage reste dans le dépôt, non routée : revenir en arrière = remettre
  // son import ici et dans App.js.
  TeamV3:           lazyPublic(() => import('./pages/TeamV3')),
  PlansV2:          lazyPublic(() => import('./pages/PlansV2')),
  // /entreprises : « Archer for Teams » v3 (design-refs/for-teams-v3).
  // TeamsPage reste dans le dépôt, non routée : revenir en arrière =
  // remettre son import ici et dans App.js.
  TeamsV3:          lazyPublic(() => import('./pages/TeamsV3')),
  LandingThemePage: lazyPublic(() => import('./pages/LandingThemePage')),
  GuidesHub:        lazyPublic(() => import('./pages/GuidesHub')),
  EnginePage:       lazyPublic(() => import('./pages/EnginePage')),
  HowItWorksV2:     lazyPublic(() => import('./pages/HowItWorksV2')),
  Blog:             lazyPublic(() => import('./pages/Blog')),
  BlogArticle:      lazyPublic(() => import('./pages/BlogArticle')),
  BePillarPage:     lazyPublic(() => import('./pages/BePillarPage')),
  LegalPage:        lazyPublic(() => import('./pages/LegalPage')),
  AttorneyApply:    lazyPublic(() => import('./pages/AttorneyApply')),
};

/** Le composant public qui sert `pathname`, ou null (route de l'app). */
export function publicPageFor(pathname) {
  const p = (pathname || '/').replace(/\/+$/, '') || '/';
  if (REDIRECTS[p]) return null;
  const seg = p.split('/').filter(Boolean);
  if (p === '/') return PUBLIC_PAGES.HomeV3;
  if (seg.length === 1) {
    const s = seg[0];
    if (s === 'attorneys') return PUBLIC_PAGES.TeamV3;
    if (s === 'plans') return PUBLIC_PAGES.PlansV2;
    if (s === 'entreprises') return PUBLIC_PAGES.TeamsV3;
    if (s === 'guides') return PUBLIC_PAGES.GuidesHub;
    if (s === 'engine') return PUBLIC_PAGES.EnginePage;
    if (s === 'how-it-works') return PUBLIC_PAGES.HowItWorksV2;
    if (s === 'blog') return PUBLIC_PAGES.Blog;
    if (BE_PILLAR_SLUGS.includes(s) || PAGES_DETRESSE_SLUGS.includes(s)) return PUBLIC_PAGES.BePillarPage;
    if (LEGAL_SLUGS.includes(s)) return PUBLIC_PAGES.LegalPage;
    if (LANDING_SLUGS.includes(s)) return PUBLIC_PAGES.LandingThemePage;
  }
  if (seg.length === 2) {
    if (seg[0] === 'blog') return PUBLIC_PAGES.BlogArticle;
    if (seg[0] === 'attorney' && seg[1] === 'apply') return PUBLIC_PAGES.AttorneyApply;
    if (seg[0] === 'how-it-works' || seg[1] === 'how-it-works') return PUBLIC_PAGES.HowItWorksV2;
  }
  return null;
}

/** Précharge le chunk de la page publique courante. Résout toujours. */
export function preloadPublicPage(pathname) {
  const Page = publicPageFor(pathname);
  if (!Page) return Promise.resolve(false);
  return Page.preload().then(() => true, () => false);
}
